import React, { FC, PropsWithChildren, useState } from "react";
import { StyledTooltip, StyledPopper } from "components/StyledUI";
import { tss } from "tss-react/mui";
import Typography from "@mui/material/Typography";

import { ReactComponent as ASCIcon } from "assets/attributeIcons/icon-merkmal-asc.svg";
import { ReactComponent as AusDerRegionIcon } from "assets/attributeIcons/icon-merkmal-aus-der-region.svg";
import { ReactComponent as BioIcon } from "assets/attributeIcons/icon-merkmal-bio-eu.svg";
import { ReactComponent as FairtradeIcon } from "assets/attributeIcons/icon-merkmal-fairtrade.svg";
import { ReactComponent as MSCIcon } from "assets/attributeIcons/icon-merkmal-msc.svg";
import HTMLReactParser from "html-react-parser";

const handleSize = (value: unknown) =>
  Number.isNaN(value) ? `${value}` : `${value}px`;

const useStyles = tss
  .withParams<Pick<StyledIconProps, "color" | "sizeX" | "sizeY">>()
  .create(({ color, sizeX, sizeY }) => ({
    root: {
      color: color || "inherit",
      display: "flex",
      "& svg": {
        height: sizeX ? handleSize(sizeX) : "16px",
        width: sizeY ? handleSize(sizeY) : "16px",
        pointerEvents: "none",
      },
    },
    popperContentContainer: {
      display: "flex",
      flexDirection: "column",
      padding: "4px 8px",
      maxWidth: "475px",
      wordWrap: "break-word",
    },
    popperItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: "20px",
      "& svg": {
        height: "40px",
        width: "50px",
        flexShrink: 0,
      },
      "& .text": {
        marginLeft: "12px",
      },
      "&:last-child": {
        marginBottom: 0,
      },
    },
    text: {
      marginLeft: "12px",
      wordWrap: "break-word",
      overflowWrap: "break-word",
    },
  }));

interface IconPopper {
  identifier: string;
  text: string;
}

const renderIcon = (identifier: string) => {
  switch (identifier) {
    case "ASC":
      return <ASCIcon />;
    case "BIO":
      return <BioIcon />;
    case "FAIRTRADE":
      return <FairtradeIcon />;
    case "MSC":
      return <MSCIcon />;
    case "REGIONAL":
      return <AusDerRegionIcon />;
    default:
      return null;
  }
};

type StyledIconProps = {
  size?: string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  tooltip?: string;
  delay?: number;
  sizeX?: string;
  sizeY?: string;
  popperContent?: IconPopper[];
};

const StyledIcon: FC<PropsWithChildren<StyledIconProps>> = ({
  children,
  className,
  color,
  size,
  tooltip,
  delay,
  sizeX,
  sizeY,
  popperContent,
  ...rest
}) => {
  const { classes, cx } = useStyles({
    color,
    sizeX: sizeX || size,
    sizeY: sizeY || size,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handlePopperOpen = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopperClose = () => {
    setAnchorEl(null);
  };

  const isPopperOpen = Boolean(anchorEl);

  return (
    <>
      {tooltip ? (
        <StyledTooltip
          title={HTMLReactParser(tooltip)}
          enterDelay={delay || 2500}
          enterNextDelay={delay || 2500}>
          <div
            className={cx(classes.root, className)}
            {...rest}
            onMouseEnter={popperContent ? handlePopperOpen : undefined}
            onMouseLeave={popperContent ? handlePopperClose : undefined}>
            {children}
          </div>
        </StyledTooltip>
      ) : (
        <div
          className={cx(classes.root, className)}
          {...rest}
          onMouseEnter={popperContent ? handlePopperOpen : undefined}
          onMouseLeave={popperContent ? handlePopperClose : undefined}>
          {children}
        </div>
      )}
      {/* Popper-Element for the greenWorld icon */}
      {popperContent && popperContent.length > 0 && (
        <StyledPopper
          open={isPopperOpen}
          anchorEl={anchorEl}
          onClose={handlePopperClose}
          placement="bottom">
          <div className={classes.popperContentContainer}>
            {popperContent.map((item) => (
              <div
                key={`${item.identifier}-${item.text}`}
                className={classes.popperItem}>
                {renderIcon(item.identifier)}
                <Typography className="text">
                  {HTMLReactParser(item.text)}
                </Typography>
              </div>
            ))}
          </div>
        </StyledPopper>
      )}
    </>
  );
};

export default StyledIcon;
